import React, { useState, useEffect } from 'react';
import { apiClient } from '../api/client';
import { usePageAuthorization } from '../hooks/usePageAuthorization';

interface SmsStatus {
  configured: boolean;
  fromNumber: string | null;
}

interface SendResult {
  success: boolean;
  sid?: string;
  error?: string;
}

interface BulkResult {
  sent: number;
  failed: number;
  results: SendResult[];
}

const styles: Record<string, React.CSSProperties> = {
  page: { padding: '2rem', maxWidth: '800px', margin: '0 auto' },
  title: { color: '#1a6b3c', fontSize: '1.75rem', margin: '0 0 0.5rem 0' },
  subtitle: { color: '#666', fontSize: '0.9rem', margin: '0 0 1.5rem 0' },
  section: {
    marginBottom: '1.5rem', padding: '1.5rem', backgroundColor: '#fff',
    border: '1px solid #e0e0e0', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  field: { marginBottom: '1rem' },
  label: { display: 'block', marginBottom: '0.25rem', fontWeight: 600, fontSize: '0.85rem', color: '#333' },
  input: {
    padding: '0.6rem 0.75rem', border: '1px solid #ccc', borderRadius: '4px',
    fontSize: '0.9rem', width: '100%', boxSizing: 'border-box' as const,
  },
  textarea: {
    padding: '0.6rem 0.75rem', border: '1px solid #ccc', borderRadius: '4px',
    fontSize: '0.9rem', width: '100%', boxSizing: 'border-box' as const,
    minHeight: '100px', resize: 'vertical' as const, fontFamily: 'inherit',
  },
  btn: {
    padding: '0.6rem 1.25rem', backgroundColor: '#1a6b3c', color: '#fff',
    border: 'none', borderRadius: '4px', fontSize: '0.9rem', fontWeight: 600,
    cursor: 'pointer',
  },
  btnDisabled: { opacity: 0.6, cursor: 'not-allowed' },
  charCount: { fontSize: '0.75rem', color: '#666', marginTop: '0.25rem', textAlign: 'right' as const },
  success: {
    padding: '0.75rem', backgroundColor: '#e8f5e9', borderRadius: '4px',
    color: '#1a6b3c', fontSize: '0.85rem', marginBottom: '1rem',
  },
  error: {
    padding: '0.75rem', backgroundColor: '#fdecea', borderRadius: '4px',
    color: '#d32f2f', fontSize: '0.85rem', marginBottom: '1rem',
  },
  warning: {
    padding: '0.75rem', backgroundColor: '#fff3e0', borderRadius: '4px',
    color: '#e65100', fontSize: '0.85rem', marginBottom: '1rem',
  },
  statusBadge: {
    display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
    padding: '0.3rem 0.75rem', borderRadius: '12px', fontSize: '0.8rem', fontWeight: 600,
  },
  tabs: { display: 'flex', gap: '0.5rem', marginBottom: '1.5rem' },
  tab: {
    padding: '0.5rem 1rem', border: 'none', borderRadius: '4px',
    fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer',
    backgroundColor: '#e0e0e0', color: '#333',
  },
  tabActive: {
    padding: '0.5rem 1rem', border: 'none', borderRadius: '4px',
    fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer',
    backgroundColor: '#1a6b3c', color: '#fff',
  },
};

type TabMode = 'single' | 'bulk';

export function SendTextPage() {
  usePageAuthorization('send-text');
  const [status, setStatus] = useState<SmsStatus | null>(null);
  const [mode, setMode] = useState<TabMode>('single');

  // Single send
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<SendResult | null>(null);

  // Bulk send
  const [bulkPhones, setBulkPhones] = useState('');
  const [bulkMessage, setBulkMessage] = useState('');
  const [bulkSending, setBulkSending] = useState(false);
  const [bulkResult, setBulkResult] = useState<BulkResult | null>(null);

  useEffect(() => {
    apiClient<SmsStatus>('/sms/status').then(setStatus).catch(() => {});
  }, []);

  async function handleSendSingle(e: React.FormEvent) {
    e.preventDefault();
    if (!phone || !message) return;
    setSending(true);
    setResult(null);
    try {
      const res = await apiClient<SendResult>('/sms/send', {
        method: 'POST',
        body: { to: phone, body: message },
      });
      setResult(res);
      if (res.success) { setPhone(''); setMessage(''); }
    } catch (err: any) {
      setResult({ success: false, error: err.message || 'Failed to send' });
    } finally {
      setSending(false);
    }
  }

  async function handleSendBulk(e: React.FormEvent) {
    e.preventDefault();
    const recipients = bulkPhones
      .split(/[\n,;]+/)
      .map((p) => p.trim())
      .filter(Boolean);
    if (recipients.length === 0 || !bulkMessage) return;
    setBulkSending(true);
    setBulkResult(null);
    try {
      const res = await apiClient<BulkResult>('/sms/send-bulk', {
        method: 'POST',
        body: { recipients, body: bulkMessage },
      });
      setBulkResult(res);
      if (res.failed === 0) { setBulkPhones(''); setBulkMessage(''); }
    } catch (err: any) {
      setBulkResult({ sent: 0, failed: 0, results: [{ success: false, error: err.message }] });
    } finally {
      setBulkSending(false);
    }
  }

  return (
    <div style={styles.page}>
      <h1 style={styles.title}>📱 Send Text Message</h1>
      <p style={styles.subtitle}>Send SMS text messages to members and attendees via Twilio</p>

      {/* Status */}
      {status && (
        <div style={{ marginBottom: '1rem' }}>
          <span style={{
            ...styles.statusBadge,
            backgroundColor: status.configured ? '#e8f5e9' : '#fdecea',
            color: status.configured ? '#1a6b3c' : '#d32f2f',
          }}>
            <span>{status.configured ? '✅' : '❌'}</span>
            {status.configured ? `Connected — From: ${status.fromNumber}` : 'Twilio not configured'}
          </span>
        </div>
      )}

      {status && !status.configured && (
        <div style={styles.warning}>
          Twilio credentials are not configured. Set <code>TWILIO_ACCOUNT_SID</code>, <code>TWILIO_AUTH_TOKEN</code>, and <code>TWILIO_PHONE_NUMBER</code> in your environment variables.
        </div>
      )}

      {/* Tabs */}
      <div style={styles.tabs}>
        <button style={mode === 'single' ? styles.tabActive : styles.tab} onClick={() => setMode('single')}>
          Single Message
        </button>
        <button style={mode === 'bulk' ? styles.tabActive : styles.tab} onClick={() => setMode('bulk')}>
          Bulk Send
        </button>
      </div>

      {/* Single Message */}
      {mode === 'single' && (
        <div style={styles.section}>
          <form onSubmit={handleSendSingle}>
            {result && (
              <div style={result.success ? styles.success : styles.error}>
                {result.success ? `✅ Message sent! SID: ${result.sid}` : `❌ ${result.error}`}
              </div>
            )}
            <div style={styles.field}>
              <label style={styles.label}>Phone Number</label>
              <input
                type="tel"
                style={styles.input}
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="(404) 555-1234 or +14045551234"
              />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Message</label>
              <textarea
                style={styles.textarea}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Type your text message here..."
                maxLength={1600}
              />
              <div style={styles.charCount}>{message.length}/1600</div>
            </div>
            <button
              type="submit"
              style={{ ...styles.btn, ...(sending || !status?.configured ? styles.btnDisabled : {}) }}
              disabled={sending || !status?.configured}
            >
              {sending ? 'Sending...' : '📤 Send Text'}
            </button>
          </form>
        </div>
      )}

      {/* Bulk Send */}
      {mode === 'bulk' && (
        <div style={styles.section}>
          <form onSubmit={handleSendBulk}>
            {bulkResult && (
              <div style={bulkResult.failed === 0 ? styles.success : styles.warning}>
                ✅ Sent: {bulkResult.sent} | ❌ Failed: {bulkResult.failed}
                {bulkResult.results.filter((r) => !r.success).map((r, i) => (
                  <div key={i} style={{ fontSize: '0.8rem', marginTop: '0.25rem' }}>— {r.error}</div>
                ))}
              </div>
            )}
            <div style={styles.field}>
              <label style={styles.label}>Phone Numbers (one per line, or comma-separated)</label>
              <textarea
                style={{ ...styles.textarea, minHeight: '80px' }}
                value={bulkPhones}
                onChange={(e) => setBulkPhones(e.target.value)}
                placeholder={"+14045551234\n+14045555678\n+14045559012"}
              />
              <div style={styles.charCount}>
                {bulkPhones.split(/[\n,;]+/).filter((p) => p.trim()).length} recipients
              </div>
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Message</label>
              <textarea
                style={styles.textarea}
                value={bulkMessage}
                onChange={(e) => setBulkMessage(e.target.value)}
                placeholder="Type your text message here..."
                maxLength={1600}
              />
              <div style={styles.charCount}>{bulkMessage.length}/1600</div>
            </div>
            <button
              type="submit"
              style={{ ...styles.btn, ...(bulkSending || !status?.configured ? styles.btnDisabled : {}) }}
              disabled={bulkSending || !status?.configured}
            >
              {bulkSending ? 'Sending...' : '📤 Send to All'}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
